import React, { useState } from 'react';
import {
    Button,
    Modal,
    Box,
    TextField,
    Typography,
    Tabs,
    Tab,
    Alert
} from '@mui/material';
import { useAuth } from '../stateManagement/AuthContext';

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`auth-tabpanel-${index}`}
            aria-labelledby={`auth-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>{children}</Box>
            )}
        </div>
    );
}

export const AuthModal = ({ open, handleClose }) => {
    const { login, register } = useAuth();
    const [tabValue, setTabValue] = useState(0); // 0 for Login, 1 for Register
    const [formData, setFormData] = useState({});
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
        setError(''); // Clear errors on tab change
        setFormData({}); // Clear form data
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setLoading(true);
        try {
            if (tabValue === 0) { // Login
                await login({ 
                    username: formData.username, // Or email if using email login 
                    password: formData.password 
                });
            } else { // Register
                if (formData.password !== formData.password2) {
                    setError("Passwords do not match.");
                    setLoading(false);
                    return;
                }
                await register({
                    username: formData.username,
                    email: formData.email,
                    password1: formData.password,
                    password2: formData.password2
                });
            }
            handleClose(); // Close modal on success
        } catch (err) {
            // Extract error message from backend if possible
            const backendError = err.response?.data;
            let displayError = 'An error occurred.';
            if (typeof backendError === 'object' && backendError !== null) {
                // Combine multiple error messages if they exist
                displayError = Object.entries(backendError)
                                     .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                                     .join(' \n');
            } else if (typeof backendError === 'string') {
                displayError = backendError;
            }
            setError(displayError || 'Login/Registration failed.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={modalStyle}>
                <Typography variant="h6" component="h2">Authentication</Typography>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="login register tabs">
                        <Tab label="Login" id="auth-tab-0" aria-controls="auth-tabpanel-0" />
                        <Tab label="Register" id="auth-tab-1" aria-controls="auth-tabpanel-1" />
                    </Tabs>
                </Box>
                <form onSubmit={handleSubmit}>
                    <TabPanel value={tabValue} index={0}>
                        {/* Login Form */}
                        <TextField margin="normal" required fullWidth id="username" label="Username or Email" name="username" autoComplete="username" autoFocus onChange={handleInputChange} />
                        <TextField margin="normal" required fullWidth name="password" label="Password" type="password" id="password" autoComplete="current-password" onChange={handleInputChange} />
                    </TabPanel>
                    <TabPanel value={tabValue} index={1}>
                        {/* Registration Form */}
                        <TextField margin="normal" required fullWidth id="reg-username" label="Username" name="username" autoComplete="username" onChange={handleInputChange} />
                        <TextField margin="normal" required fullWidth id="reg-email" label="Email Address" name="email" autoComplete="email" onChange={handleInputChange} />
                        <TextField margin="normal" required fullWidth name="password" label="Password" type="password" id="reg-password" onChange={handleInputChange} 
                          helperText="Password must be at least 8 characters, contain letters and numbers, and not be too common." />
                        <TextField margin="normal" required fullWidth name="password2" label="Confirm Password" type="password" id="reg-password2" onChange={handleInputChange} />
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                          Password must be at least 8 characters, contain letters and numbers, and not be too common.
                        </Typography>
                    </TabPanel>
                    {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                    <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }} disabled={loading}>
                        {loading ? 'Processing...' : (tabValue === 0 ? 'Sign In' : 'Register')}
                    </Button>
                </form>
            </Box>
        </Modal>
    );
}; 